import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"
import { Copy, Check } from "lucide-react"

type SplitMethod = "Equal" | "By Room Size" | "By Occupants" | "Custom %"

interface TenantSplit {
  id: number
  name: string
  roomSize: number
  occupants: number
  customPct: number
}

export default function UtilitiesCostSplitterPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [totalBill, setTotalBill] = useState(300)
  const [billPeriod, setBillPeriod] = useState<"Monthly" | "Quarterly">("Monthly")
  const [billType, setBillType] = useState("Electricity")
  const [numTenants, setNumTenants] = useState(3)
  const [splitMethod, setSplitMethod] = useState<SplitMethod>("Equal")
  const [landlordShare, setLandlordShare] = useState(0)

  // Manage array of tenants
  const [tenants, setTenants] = useState<TenantSplit[]>(
    Array.from({ length: 10 }, (_, i) => ({ id: i + 1, name: `Tenant ${i + 1}`, roomSize: 100, occupants: 1, customPct: 100 / 10 }))
  )

  const activeTenants = tenants.slice(0, numTenants)

  const updateTenant = (id: number, key: keyof TenantSplit, value: any) => {
    setTenants(tenants.map(t => t.id === id ? { ...t, [key]: value } : t))
  }

  // Basic computations
  const monthlyBill = billPeriod === "Quarterly" ? totalBill / 3 : totalBill
  const landlordAmount = monthlyBill * (landlordShare / 100)
  const tenantPool = monthlyBill - landlordAmount

  const totalRoomSize = activeTenants.reduce((sum, t) => sum + t.roomSize, 0)
  const totalOccupants = activeTenants.reduce((sum, t) => sum + t.occupants, 0)
  const totalCustomPct = activeTenants.reduce((sum, t) => sum + t.customPct, 0)

  // Map results
  const results = activeTenants.map(t => {
    let amount = 0
    if (splitMethod === "Equal") amount = tenantPool / numTenants
    else if (splitMethod === "By Room Size") amount = (t.roomSize / (totalRoomSize || 1)) * tenantPool
    else if (splitMethod === "By Occupants") amount = (t.occupants / (totalOccupants || 1)) * tenantPool
    else if (splitMethod === "Custom %") amount = (t.customPct / 100) * tenantPool

    return { ...t, amount }
  })

  // Copy to clipboard feature
  const [copied, setCopied] = useState(false)
  
  const textOutput = useMemo(() => {
    const lines = [`Monthly ${billType} bill split:`]
    results.forEach(r => lines.push(`${r.name}: ${currency}${r.amount.toFixed(2)}`))
    if (landlordAmount > 0) lines.push(`Landlord Share: ${currency}${landlordAmount.toFixed(2)}`)
    lines.push(`Total Assessed: ${currency}${monthlyBill.toFixed(2)}`)
    return lines.join('\n')
  }, [results, landlordAmount, monthlyBill, billType])

  const copyToClipboard = () => {
    navigator.clipboard.writeText(textOutput)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <ToolShell>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-8">
        
        {/* Inputs */}
        <div className="lg:col-span-7 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border border-slate-200 p-5 rounded-xl bg-slate-50">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Total Utility Bill</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={totalBill || ''} onChange={(e) => setTotalBill(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bill Period</label>
              <select value={billPeriod} onChange={(e) => setBillPeriod(e.target.value as any)} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary bg-white">
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly (divides by 3)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bill Type</label>
              <select value={billType} onChange={(e) => setBillType(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary bg-white">
                <option value="Electricity">Electricity</option>
                <option value="Gas">Gas</option>
                <option value="Water">Water</option>
                <option value="Internet">Internet</option>
                <option value="Combined">Combined All</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Landlord Common Area Share: {landlordShare}%</label>
              <input type="range" min="0" max="50" step="5" value={landlordShare} onChange={(e) => setLandlordShare(Number(e.target.value))} className="w-full accent-primary" />
            </div>
          </div>

          <div className="border border-slate-200 p-5 rounded-xl bg-white space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-slate-100 pb-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Number of Tenants / Units</label>
                <input type="number" min="2" max="10" value={numTenants} onChange={(e) => setNumTenants(Math.max(2, Math.min(10, Number(e.target.value))))} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Split Method</label>
                <select value={splitMethod} onChange={(e) => setSplitMethod(e.target.value as any)} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary">
                  <option value="Equal">Equal Share</option>
                  <option value="By Room Size">By Room/Unit Size</option>
                  <option value="By Occupants">By Occupants</option>
                  <option value="Custom %">Custom Percentage</option>
                </select>
              </div>
            </div>

            {splitMethod === "Custom %" && totalCustomPct !== 100 && (
              <div className="bg-amber-50 text-amber-700 p-3 rounded text-sm mb-4 border border-amber-200">
                Warning: Percentages currently sum to {totalCustomPct}%. They must sum to 100% for an accurate split.
              </div>
            )}

            <div className="space-y-3">
              <h4 className="font-medium text-slate-800 text-sm">Tenant Configurations</h4>
              {activeTenants.map((t, idx) => (
                <div key={t.id} className="flex flex-col sm:flex-row gap-3 items-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <input type="text" value={t.name} onChange={(e) => updateTenant(t.id, 'name', e.target.value)} className="w-full sm:w-1/3 px-3 py-1.5 rounded border border-slate-300 text-sm" />
                  
                  {splitMethod === "By Room Size" && (
                    <div className="w-full sm:w-2/3 relative">
                      <span className="absolute right-3 top-1.5 text-slate-400 text-sm">sqm/sqft</span>
                      <input type="number" value={t.roomSize} onChange={(e) => updateTenant(t.id, 'roomSize', Number(e.target.value))} className="w-full pl-3 pr-16 py-1.5 rounded border border-slate-300 text-sm" />
                    </div>
                  )}
                  {splitMethod === "By Occupants" && (
                    <div className="w-full sm:w-2/3 relative">
                      <span className="absolute right-3 top-1.5 text-slate-400 text-sm">people</span>
                      <input type="number" min="1" value={t.occupants} onChange={(e) => updateTenant(t.id, 'occupants', Number(e.target.value))} className="w-full pl-3 pr-16 py-1.5 rounded border border-slate-300 text-sm" />
                    </div>
                  )}
                  {splitMethod === "Custom %" && (
                    <div className="w-full sm:w-2/3 relative">
                      <span className="absolute right-3 top-1.5 text-slate-400 text-sm">%</span>
                      <input type="number" value={t.customPct} onChange={(e) => updateTenant(t.id, 'customPct', Number(e.target.value))} className="w-full pl-3 pr-16 py-1.5 rounded border border-slate-300 text-sm" />
                    </div>
                  )}
                  {splitMethod === "Equal" && (
                    <div className="w-full sm:w-2/3 text-sm text-slate-500 bg-white border border-slate-200 py-1.5 px-3 rounded">
                      Automatic Equal Split
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-gradient-to-b from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-lg overflow-hidden relative">
            <h4 className="font-semibold text-slate-200 mb-4 border-b border-white/10 pb-2">Final Invoice Split</h4>
            
            <div className="space-y-3 relative z-10">
              {results.map((r) => (
                <div key={r.id} className="flex justify-between items-center text-sm py-1">
                  <span className="text-slate-300 font-medium">{r.name}</span>
                  <span className="font-bold text-white bg-white/10 px-3 py-1 rounded">{currency}{r.amount.toFixed(2)}</span>
                </div>
              ))}
              
              {landlordAmount > 0 && (
                <div className="flex justify-between items-center text-sm mt-4 pt-4 border-t border-white/10">
                  <span className="text-primary-300">Landlord Share ({landlordShare}%)</span>
                  <span className="font-bold text-primary-300 drop-shadow-sm">{currency}{landlordAmount.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            <div className="mt-6">
              <div className="bg-slate-950 p-4 rounded-lg text-xs font-mono text-slate-400 whitespace-pre-line leading-relaxed mb-3 border border-white/5">
                {textOutput}
              </div>
              <button 
                onClick={copyToClipboard}
                className="w-full flex items-center justify-center py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied text!" : "Copy values to clipboard"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 pb-10">
        <FormulaBox 
          title="How are splits calculated?"
          formula={`Pool = Monthly Bill - (Monthly Bill × Landlord Pct)\n\nEqual = Pool / Users\nRoom Size = Pool × (User Room Area / Total Area)\nOccupants = Pool × (User Occupants / Total Occupants)\nCustom = Pool × (User Pct / 100)`} 
        />
        <CTABox icon={tool.icon} heading="Invoice tenants automatically" body={tool.ctaText} ctaLabel="Use RentKeep Free" ctaHref="/register" />
        <ToolFAQ 
          items={[
            { q: "Is it legal to split utility bills unequally?", a: "In HMOs (Houses in Multiple Occupation), landlords often split by room size or occupancy count via the tenancy agreement to ensure fairness, especially if one room is a double and another is a single. Always ensure your lease agreement stipulates the exact calculation method used." },
            { q: "Why would the landlord pay a share?", a: "If the utility meter covers landlord-operated common areas (like a shared laundry room, hallway lighting, or complex-wide irrigation), the landlord should absorb a proportional percentage of that utility bill." }
          ]} 
        />
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
